import { Button as BaseButton } from '@base-ui/react/button';
import type { BaseUIEvent } from '@base-ui/react/types';
import type { LucideIcon } from 'lucide-react';
import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { buttonVariants } from '@/components/ui/Button';
import { cx, type VariantProps } from '@/cva.config';

interface AsyncActionBtnProps
  extends VariantProps<typeof buttonVariants>,
    Omit<ComponentProps<typeof BaseButton>, 'disabled'> {
  animation?: 'spin' | 'pulse';
  children?: ReactNode;
  disabled?: boolean;
  icon?: LucideIcon;
  isPending?: boolean;
  onAction?: () => Promise<unknown>;
}

export const AsyncActionBtn = ({
  animation = 'spin',
  children,
  className,
  disabled = false,
  fullWidth,
  icon: IconComponent,
  isPending,
  onAction,
  onClick,
  size = 'sm',
  variant,
  ...rest
}: AsyncActionBtnProps) => {
  const [internalBusy, setInternalBusy] = useState(false);

  const isBusy = isPending ?? internalBusy;

  const handleClick = (event: BaseUIEvent<MouseEvent<HTMLButtonElement>>) => {
    if (isBusy) return;
    onClick?.(event);
    if (!onAction) return;
    setInternalBusy(true);
    onAction()
      .catch((error: unknown) => {
        console.error('[AsyncActionBtn] action failed:', error);
      })
      .finally(() => setInternalBusy(false));
  };

  return (
    <BaseButton
      {...rest}
      aria-busy={isBusy || undefined}
      className={cx(buttonVariants({ fullWidth, size, variant }), className)}
      disabled={disabled || isBusy}
      onClick={handleClick}
      type="button"
    >
      {IconComponent && (
        <IconComponent
          aria-hidden
          className={cx(
            'size-3.5',
            isBusy
              && (animation === 'pulse' ? 'animate-pulse' : 'animate-spin'),
          )}
        />
      )}
      {children}
    </BaseButton>
  );
};
